import React from 'react';
import { Select, Button, Divider, Checkbox, Input, Switch } from 'antd';
import { CheckCircleTwoTone, CloseCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import ConfigurationLayout from '../configuration.layout';
import styles from './styles/segmentation-step.module.scss';

const { Option } = Select;

function SegmentationStep({
    state,
    firstTimeSetup,
    setBankingTenure,
    setAccountTypes,
    setAvgDailyBalance,
    setOverdraftHistory,
    setReturnedACHs,
    toggleFieldEnabled,
    setSegmentationMode,
    setWhitelistInput,
    addToWhitelist,
    removeFromWhitelist,
    setBlockOutOfSegmentation,
    getEligibilitySummary,
    hasChanges,
    onSubmit,
    onContinue,
    onCancel,
    onStepChange
}) {
    const { segment_policy, segmentation_enabled, loading, whitelistInput, user } = state;
    const {
        mode,
        whitelist,
        bankingTenure,
        accountTypes,
        avgDailyBalance,
        overdraftHistory,
        returnedACHs,
        blockOutOfSegmentation = false
    } = segment_policy || {};
    const eligibilitySummary = getEligibilitySummary();

    const handleAddToWhitelist = () => {
        if (whitelistInput && whitelistInput.trim()) {
            const memberId = whitelistInput.trim();
            if (!whitelist.includes(memberId)) {
                addToWhitelist(memberId);
                setWhitelistInput('');
            }
        }
    };

    // Main content - form section
    const mainContent = (
        <div className={styles.formSection}>
            {/* Segmentation Mode Selector */}
            <div className={styles.modeSection}>
                <h2 className={styles.modeSectionTitle}>Who Has Access To The Service</h2>
                <div className={styles.modeButtonGroup}>
                    <Button
                        type={mode === 'disabled' ? 'default' : 'default'}
                        className={`${styles.modeButton} ${mode === 'disabled' ? styles.modeButtonActive : ''}`}
                        onClick={() => setSegmentationMode('disabled')}>
                        Disabled
                    </Button>
                    <Button
                        type={mode === 'whitelist' ? 'default' : 'default'}
                        className={`${styles.modeButton} ${mode === 'whitelist' ? styles.modeButtonActive : ''}`}
                        onClick={() => setSegmentationMode('whitelist')}>
                        Whitelist
                    </Button>
                    <Button
                        type={mode === 'restricted' ? 'default' : 'default'}
                        className={`${styles.modeButton} ${mode === 'restricted' ? styles.modeButtonActive : ''}`}
                        onClick={() => setSegmentationMode('restricted')}>
                        Restricted
                    </Button>
                    <Button
                        type={mode === 'open' ? 'default' : 'default'}
                        className={`${styles.modeButton} ${mode === 'open' ? styles.modeButtonActive : ''}`}
                        onClick={() => setSegmentationMode('open')}>
                        Open
                    </Button>
                </div>
            </div>

            {/* Block Out of Segmentation Switch */}
            {mode === 'restricted' && (
                <div className={styles.blockSwitchSection}>
                    <div className={styles.blockSwitchContent}>
                        <div className={styles.blockSwitchLabel}>
                            <label className={styles.blockSwitchTitle}>Block users who fall out of segmentation</label>
                            <p className={styles.blockSwitchDescription}>
                                When enabled, any user that falls out of the segmentation criteria at any point will be unable to use the service.
                            </p>
                        </div>
                        <Switch checked={blockOutOfSegmentation} onChange={setBlockOutOfSegmentation} size="default" />
                    </div>
                </div>
            )}

            {/* Disabled Mode */}
            {mode === 'disabled' && (
                <div className={styles.disabledMode}>
                    <div className={styles.disabledGraphic}>
                        <CloseCircleOutlined className={styles.disabledIcon} />
                    </div>
                    <h3 className={styles.disabledTitle}>Service Disabled</h3>
                    <p className={styles.disabledDescription}>The Earned Wage Access service is currently disabled. No users can access the service.</p>
                </div>
            )}

            {/* Whitelist Mode */}
            {mode === 'whitelist' && (
                <div className={styles.whitelistMode}>
                    <h3 className={styles.whitelistTitle}>Member Whitelist</h3>
                    <p className={styles.whitelistDescription}>Add member IDs to grant specific users access to the service.</p>

                    <div className={styles.whitelistInputGroup}>
                        <Input
                            size="large"
                            placeholder="Enter member ID..."
                            value={whitelistInput}
                            onChange={(e) => setWhitelistInput(e.target.value)}
                            onPressEnter={handleAddToWhitelist}
                            className={styles.whitelistInput}
                        />
                        <Button type="primary" size="large" onClick={handleAddToWhitelist} className={styles.addButton}>
                            Add
                        </Button>
                    </div>

                    {whitelist && whitelist.length > 0 && (
                        <div className={styles.whitelistItems}>
                            <div className={styles.whitelistHeader}>Whitelisted Members ({whitelist.length})</div>
                            <div className={styles.whitelistList}>
                                {whitelist.map((memberId, index) => (
                                    <div key={index} className={styles.whitelistItem}>
                                        <span className={styles.memberId}>{memberId}</span>
                                        <Button
                                            type="text"
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeFromWhitelist(memberId)}
                                            className={styles.removeButton}
                                            danger
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Open Mode */}
            {mode === 'open' && (
                <div className={styles.openMode}>
                    <div className={styles.openGraphic}>
                        <CheckCircleTwoTone twoToneColor="#52c41a" className={styles.openIcon} />
                    </div>
                    <h3 className={styles.openTitle}>Open Access</h3>
                    <p className={styles.openDescription}>All users have access to the Earned Wage Access service without restrictions.</p>
                </div>
            )}

            {/* Restricted Mode - Existing Form */}
            {mode === 'restricted' && (
                <>
                    <h2 className={styles.sectionTitle}>Segment the users that will have access to the service</h2>
                    <div className={styles.formFields}>
                        {/* Banking Tenure */}
                        <div className={`${styles.fieldGroup} ${!segmentation_enabled.bankingTenure ? styles.disabled : ''}`}>
                            <div className={styles.fieldHeader}>
                                <Checkbox
                                    checked={segmentation_enabled.bankingTenure}
                                    onChange={() => toggleFieldEnabled('bankingTenure')}
                                    className={styles.fieldCheckbox}
                                />
                                <label className={styles.fieldLabel}>Banking tenure</label>
                            </div>
                            <p className={styles.fieldDescription}>Your eligible customer's amount of time banking at your institution</p>
                            <Select
                                value={bankingTenure}
                                onChange={setBankingTenure}
                                className={styles.selectField}
                                size="large"
                                disabled={!segmentation_enabled.bankingTenure}>
                                <Option value="more-than-30-days">More than 30 days</Option>
                                <Option value="more-than-60-days">More than 60 days</Option>
                                <Option value="more-than-90-days">More than 90 days</Option>
                                <Option value="more-than-180-days">More than 180 days</Option>
                                <Option value="more-than-365-days">More than 365 days</Option>
                            </Select>
                        </div>

                        {/* Account Types */}
                        <div className={`${styles.fieldGroup} ${!segmentation_enabled.accountTypes ? styles.disabled : ''}`}>
                            <div className={styles.fieldHeader}>
                                <Checkbox
                                    checked={segmentation_enabled.accountTypes}
                                    onChange={() => toggleFieldEnabled('accountTypes')}
                                    className={styles.fieldCheckbox}
                                />
                                <label className={styles.fieldLabel}>Account Types</label>
                            </div>
                            <p className={styles.fieldDescription}>The account types your eligible customers have at your institution.</p>
                            <Select
                                value={accountTypes}
                                onChange={setAccountTypes}
                                className={styles.selectField}
                                size="large"
                                disabled={!segmentation_enabled.accountTypes}>
                                <Option value="any">Any</Option>
                                <Option value="checking-only">At least Checking</Option>
                                <Option value="savings-only">At least Savings</Option>
                                <Option value="both-types">Checking & Savings</Option>
                            </Select>
                        </div>

                        {/* Avg Daily Balance */}
                        <div className={`${styles.fieldGroup} ${!segmentation_enabled.avgDailyBalance ? styles.disabled : ''}`}>
                            <div className={styles.fieldHeader}>
                                <Checkbox
                                    checked={segmentation_enabled.avgDailyBalance}
                                    onChange={() => toggleFieldEnabled('avgDailyBalance')}
                                    className={styles.fieldCheckbox}
                                />
                                <label className={styles.fieldLabel}>Avg Daily Balance</label>
                            </div>
                            <p className={styles.fieldDescription}>The daily average balance amount your eligible customers have at your institution.</p>
                            <Select
                                value={avgDailyBalance}
                                onChange={setAvgDailyBalance}
                                className={styles.selectField}
                                size="large"
                                disabled={!segmentation_enabled.avgDailyBalance}>
                                <Option value="less-than-500">Less than $500</Option>
                                <Option value="less-than-1000">Less than $1,000</Option>
                                <Option value="less-than-2500">Less than $2,500</Option>
                                <Option value="less-than-5000">Less than $5,000</Option>
                            </Select>
                        </div>

                        {/* Overdraft History */}
                        <div className={`${styles.fieldGroup} ${!segmentation_enabled.overdraftHistory ? styles.disabled : ''}`}>
                            <div className={styles.fieldHeader}>
                                <Checkbox
                                    checked={segmentation_enabled.overdraftHistory}
                                    onChange={() => toggleFieldEnabled('overdraftHistory')}
                                    className={styles.fieldCheckbox}
                                />
                                <label className={styles.fieldLabel}>Overdraft / NSF History</label>
                            </div>
                            <p className={styles.fieldDescription}>
                                The number of overdraft or NSF instances your eligible customers have done in the past 180 days.
                            </p>
                            <Select
                                value={overdraftHistory}
                                onChange={setOverdraftHistory}
                                className={styles.selectField}
                                size="large"
                                disabled={!segmentation_enabled.overdraftHistory}>
                                <Option value="less-than-1">Less than 1</Option>
                                <Option value="less-than-2">Less than 2</Option>
                                <Option value="less-than-4">Less than 4</Option>
                                <Option value="less-than-6">Less than 6</Option>
                            </Select>
                        </div>

                        {/* Returned ACHs */}
                        <div className={`${styles.fieldGroup} ${!segmentation_enabled.returnedACHs ? styles.disabled : ''}`}>
                            <div className={styles.fieldHeader}>
                                <Checkbox
                                    checked={segmentation_enabled.returnedACHs}
                                    onChange={() => toggleFieldEnabled('returnedACHs')}
                                    className={styles.fieldCheckbox}
                                />
                                <label className={styles.fieldLabel}>Returned ACHs or chargebacks</label>
                            </div>
                            <p className={styles.fieldDescription}>
                                The number of Return ACH or chargeback instances your eligible customers have done in the past 180 days.
                            </p>
                            <Select
                                value={returnedACHs}
                                onChange={setReturnedACHs}
                                className={styles.selectField}
                                size="large"
                                disabled={!segmentation_enabled.returnedACHs}>
                                <Option value="less-than-1">Less than 1</Option>
                                <Option value="less-than-2">Less than 2</Option>
                                <Option value="less-than-3">Less than 3</Option>
                                <Option value="less-than-5">Less than 5</Option>
                            </Select>
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    // Side content - summary and actions
    const sideContent = (
        <div className={styles.sideContent}>
            {!firstTimeSetup && (
                <div className={styles.actionSection}>
                    <Divider className={styles.actionDivider} />
                    {hasChanges() && (
                        <div className={styles.actionButtons}>
                            <Button size="large" className={styles.cancelButton} onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button type="primary" size="large" loading={loading} onClick={onSubmit} className={styles.continueButton}>
                                Update
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {firstTimeSetup && (
                <div className={styles.actionSection}>
                    <Divider className={styles.actionDivider} />
                    <div className={styles.actionButtons}>
                        <Button size="large" className={styles.cancelButton} onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="primary" size="large" loading={loading} onClick={onContinue} className={styles.continueButton}>
                            Continue
                        </Button>
                    </div>
                </div>
            )}

            <div className={styles.summarySection}>
                <div className={styles.summaryIcon}>
                    <CheckCircleTwoTone />
                </div>
                <h3 className={styles.summaryTitle}>User Eligibility Summary</h3>
                <p className={styles.summaryDescription}>These are the users that will have access to your EWA Service</p>

                <ul className={styles.summaryList}>
                    {eligibilitySummary.map((item, index) => (
                        <li key={index} className={styles.summaryItem}>
                            {item}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );

    return (
        <ConfigurationLayout isFirstTimeSetUp={firstTimeSetup} currentStep={0} sideContent={sideContent} onStepChange={onStepChange} user={user}>
            {mainContent}
        </ConfigurationLayout>
    );
}

export default SegmentationStep;
